import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { getJeepRallyMediaUrl } from "../../../../../api/jeep-rally/api";
import { useGetRouteById } from "../../../../../api/jeep-rally/routes/routes.hooks";
import {
  getRouteApiErrorMessage,
  normalizeRoute,
} from "../utils/route.utils";

const formatPoint = (point) => {
  if (!point) return "-";
  const title = point.title || "-";
  if (point.lat == null || point.lng == null) return title;
  return `${title} (${point.lat}, ${point.lng})`;
};

const RouteViewPage = () => {
  const history = useHistory();
  const { id } = useParams();

  const locationRoute = history.location.state?.route || null;

  const {
    data: routeData,
    isLoading,
    isError,
    error,
  } = useGetRouteById(id);

  const route = normalizeRoute(routeData) || locationRoute;
  const stages = Array.isArray(route?.stages) ? route.stages : [];
  const checkpoints = Array.isArray(route?.checkpoints) ? route.checkpoints : [];
  const trackMapUrl = getJeepRallyMediaUrl(route?.track_map_image);
  const isActive = route?.status !== "inactive";

  const goBack = () => history.push("/jeep-rally/routes");

  if (isLoading && !locationRoute) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isError && !locationRoute) || !route) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getRouteApiErrorMessage(
                error,
                "Route not found. Please open it from the Routes list."
              )}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to routes list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">View Route</h4>
          </div>
        </div>

        <div className="card-body">
          <Card className="shadow-none mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">{route.title || route.name || "-"}</h5>
                  <div className="text-muted">
                    Route ID: <code>{id}</code>
                  </div>
                </div>
                <Badge bg={isActive ? "success" : "secondary"}>
                  {route.status || "active"}
                </Badge>
              </div>

              <div className="d-flex gap-3 flex-wrap mb-3">
                <Badge bg="secondary">
                  Distance: {route.total_distance_km ?? "-"} km
                </Badge>
                <Badge bg="info">
                  Estimated: {route.estimated_time || "-"}
                </Badge>
                <Badge bg="light" text="dark">
                  Stages: {stages.length}
                </Badge>
                <Badge bg="light" text="dark">
                  Checkpoints: {checkpoints.length}
                </Badge>
              </div>

              <div className="mb-3">
                <div className="text-muted mb-1">Description</div>
                <div>{route.description || "-"}</div>
              </div>

              {trackMapUrl ? (
                <div className="mb-0">
                  <div className="text-muted mb-2">Track Map</div>
                  <img
                    src={trackMapUrl}
                    alt="Track map"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 320,
                      borderRadius: 6,
                      border: "1px solid #dee2e6",
                    }}
                  />
                </div>
              ) : null}
            </Card.Body>
          </Card>

          <h5 className="mb-3">Stages ({stages.length})</h5>
          {stages.length === 0 ? (
            <p className="text-muted">No stages on this route.</p>
          ) : (
            stages.map((stage, index) => {
              const stageId = stage._id || stage.id || index;
              const starting =
                stage.startingPoint || stage.starting_point || null;
              const ending = stage.endingPoint || stage.ending_point || null;

              return (
                <Card key={stageId} className="shadow-none mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">
                        Stage {stage.stage_number ?? index + 1}
                        {stage.stage_name ? ` — ${stage.stage_name}` : ""}
                      </h6>
                    </div>
                    <div className="mb-1">
                      <span className="text-muted">Start: </span>
                      {formatPoint(starting)}
                    </div>
                    <div className="mb-1">
                      <span className="text-muted">End: </span>
                      {formatPoint(ending)}
                    </div>
                    <div>
                      <span className="text-muted">Description: </span>
                      {stage.stage_description || "-"}
                    </div>
                  </Card.Body>
                </Card>
              );
            })
          )}

          <h5 className="mb-3 mt-4">Checkpoints ({checkpoints.length})</h5>
          {checkpoints.length === 0 ? (
            <p className="text-muted">No checkpoints on this route.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Stage</th>
                    <th>Order</th>
                    <th>KM Start</th>
                    <th>KM End</th>
                  </tr>
                </thead>
                <tbody>
                  {checkpoints.map((checkpoint, index) => {
                    const checkpointId =
                      checkpoint._id || checkpoint.id || index;
                    return (
                      <tr key={checkpointId}>
                        <td>{index + 1}</td>
                        <td>{checkpoint.title || "-"}</td>
                        <td>
                          {checkpoint.stage_number ??
                            checkpoint.stage_id ??
                            "-"}
                        </td>
                        <td>{checkpoint.order ?? "-"}</td>
                        <td>{checkpoint.km_start ?? "-"}</td>
                        <td>{checkpoint.km_end ?? "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex justify-content-end mt-4">
            <Button variant="outline-secondary" onClick={goBack}>
              Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteViewPage;
